import type { PeopleResponse, PersonResponse, PlanningResponse } from '@pulse/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

/** People's Everyone tab for a business date (PPL-1 to PPL-3). */
export function usePeople(date: string, today: string) {
  return useQuery({
    queryKey: ['pages', 'people', date],
    queryFn: () => apiRequest<PeopleResponse>('/api/pages/people', { searchParams: { date: date === today ? undefined : date } }),
    staleTime: 60_000,
  });
}

/** One member's day (PER-1 to PER-5). */
export function usePerson(userId: string, date: string, today: string) {
  return useQuery({
    queryKey: ['pages', 'person', userId, date],
    queryFn: () => apiRequest<PersonResponse>(`/api/pages/people/${encodeURIComponent(userId)}`, { searchParams: { date: date === today ? undefined : date } }),
    staleTime: 60_000,
  });
}

/** People's Planning gaps tab, as of now (PPL-4). */
export function usePlanning() {
  return useQuery({
    queryKey: ['pages', 'people', 'planning'],
    queryFn: () => apiRequest<PlanningResponse>('/api/pages/people/planning'),
    staleTime: 60_000,
  });
}

/** Saves the signed-in member's note for today, or clears it when empty (NOTE-1 to NOTE-3). */
export function useSaveNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => apiRequest<{ message: string; note: { text: string } | null }>('/api/pages/notes/today', { method: 'PUT', body: { text } }),
    // The note shows on Person, Today and People.
    onSuccess: () => client.invalidateQueries({ queryKey: ['pages'] }),
  });
}
